import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api, { getAuthHeader as getApiAuthHeader } from '@/lib/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage with proper error handling
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error('Failed to parse user from localStorage:', e);
      localStorage.removeItem('user');
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [hasTransportAllocation, setHasTransportAllocation] = useState(false);
  const [hasHostelAllocation, setHasHostelAllocation] = useState(false);
  
  // Track initialization to prevent double fetching
  const initializedRef = useRef(false);
  const fetchingRef = useRef(false);

  // Initial auth check on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      try {
        localStorage.setItem('user', JSON.stringify(user));
      } catch (e) {
        console.error('Failed to save user to localStorage:', e);
      }
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  // Sync token state with localStorage
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    }
  }, [token]);

  // Fetch subscription status for school users
  const fetchSubscriptionStatus = async (schoolId, authToken) => {
    if (!schoolId) return;
    try {
      const response = await api.get(`/api/schools/${schoolId}/subscription-status`);
      setSubscriptionStatus(response.data);
      setIsReadOnly(response.data.is_read_only || false);
    } catch (error) {
      // Don't block login if subscription check fails
      console.warn('Failed to fetch subscription status:', error);
      setSubscriptionStatus(null);
      setIsReadOnly(false);
    }
  };

  // Fetch user permissions for staff role-based menu filtering
  const fetchUserPermissions = async (authToken) => {
    try {
      const response = await api.get(`/api/staff-roles/my-permissions`);
      setUserPermissions(response.data);
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch user permissions:', error);
      setUserPermissions(null);
      return null;
    }
  };

  // Check if student/parent has transport allocation
  const checkTransportAllocation = async (userData, authToken) => {
    if (!['student', 'parent'].includes(userData.role)) {
      setHasTransportAllocation(true); // Admin/teacher/staff always see transport menu
      return;
    }
    
    try {
      if (userData.role === 'student') {
        // Check student's own transport
        const res = await api.get(`/api/students/me`);
        const studentId = res.data?.id;
        if (studentId) {
          const transportRes = await api.get(`/api/transport-v2/student/${studentId}`);
          setHasTransportAllocation(!!transportRes.data?.route_id);
        } else {
          setHasTransportAllocation(false);
        }
      } else if (userData.role === 'parent') {
        // Check children's transport - now included in children response
        const childrenRes = await api.get(`/api/students/parent/children`);
        const children = childrenRes.data || [];
        // Check if any child has transport allocation
        const hasTransport = children.some(child => child.has_transport || child.route_id);
        setHasTransportAllocation(hasTransport);
      }
    } catch (error) {
      console.warn('Failed to check transport allocation:', error);
      setHasTransportAllocation(false);
    }
  };

  // Check if student/parent has hostel allocation (for gate pass menu)
  const checkHostelAllocation = async (userData, authToken) => {
    if (!['student', 'parent'].includes(userData.role)) {
      setHasHostelAllocation(true); // Admin/teacher/staff always see gate pass menu
      return;
    }
    
    try {
      if (userData.role === 'student') {
        const res = await api.get(`/api/students/me`);
        // Check if student has hostel allocation
        setHasHostelAllocation(!!res.data?.is_hosteller || !!res.data?.hostel_room || !!res.data?.room_id);
      } else if (userData.role === 'parent') {
        const childrenRes = await api.get(`/api/students/parent/children`);
        const children = childrenRes.data || [];
        // Check if any child has hostel allocation - now included in children response
        const hasHostel = children.some(child => child.is_hosteller || child.hostel_room || child.room_id);
        setHasHostelAllocation(hasHostel);
      }
    } catch (error) {
      console.warn('Failed to check hostel allocation:', error);
      setHasHostelAllocation(false);
    }
  };

  const fetchCurrentUser = async (retryAttempt = 0) => {
    const MAX_RETRIES = 2;
    
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    try {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        fetchingRef.current = false;
        return;
      }
      
      const response = await api.get(`/api/auth/me`, {
        timeout: 10000 // 10 second timeout
      });
      
      setUser(response.data);
      setToken(storedToken);
      
      // Fetch subscription status for school users (not super_admin)
      if (response.data.school_id && response.data.role !== 'super_admin') {
        await fetchSubscriptionStatus(response.data.school_id, storedToken);
      }
      
      // Fetch user permissions for menu filtering
      await fetchUserPermissions(storedToken);
      
      // Check transport allocation for student/parent
      await checkTransportAllocation(response.data, storedToken);
      
      // Check hostel allocation for student/parent (for gate pass)
      await checkHostelAllocation(response.data, storedToken);
    } catch (error) {
      console.error(`Failed to fetch user (attempt ${retryAttempt + 1}):`, error);
      
      // 401/403 errors are handled by axios interceptor
      // Only retry on network errors or timeouts
      if (!error.response && retryAttempt < MAX_RETRIES) {
        console.log(`Retrying auth request... (${retryAttempt + 1}/${MAX_RETRIES})`);
        fetchingRef.current = false;
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchCurrentUser(retryAttempt + 1);
      } else if (!error.response) {
        // Network error after max retries - keep cached user if available
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch (e) {
            setUser(null);
          }
        }
      }
      // Don't clear user here - axios interceptor handles 401
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const login = async (email, password, schoolCode = null, expectedRole = null) => {
    try {
      const response = await api.post(`/api/auth/login`, {
        email,
        password,
        school_code: schoolCode,
        expected_role: expectedRole
      });
      const { user: userData, token: authToken } = response.data;
      
      // Store token first for subsequent API calls
      localStorage.setItem('token', authToken);
      setToken(authToken);
      setUser(userData);
      
      // Store login source for proper logout redirect
      // If schoolCode provided, user logged in via school-specific page
      if (schoolCode) {
        localStorage.setItem('login_source', `/${schoolCode}`);
      } else {
        localStorage.setItem('login_source', '/auth');
      }
      
      // Fetch subscription status for school users (not super_admin)
      if (userData.school_id && userData.role !== 'super_admin') {
        await fetchSubscriptionStatus(userData.school_id, authToken);
      }
      
      // Fetch user permissions for menu filtering
      await fetchUserPermissions(authToken);
      
      // Check transport allocation for student/parent
      await checkTransportAllocation(userData, authToken);
      
      // Check hostel allocation for student/parent
      await checkHostelAllocation(userData, authToken);
      
      toast.success('Login successful!');
      return userData;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post(`/api/auth/register`, userData);
      const { user: newUser, token: authToken } = response.data;
      
      localStorage.setItem('token', authToken);
      setToken(authToken);
      setUser(newUser);
      
      toast.success('Registration successful!');
      return newUser;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      throw error;
    }
  };

  const logout = useCallback(() => {
    // Get login_source before clearing (for redirect)
    const loginSource = localStorage.getItem('login_source') || '/';
    
    // Clear state
    setUser(null);
    setToken(null);
    setSubscriptionStatus(null);
    setIsReadOnly(false);
    setUserPermissions(null);
    setHasTransportAllocation(false);
    setHasHostelAllocation(false);
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('school_code');
    localStorage.removeItem('login_source');
    sessionStorage.removeItem('auth_retry_count');
    
    // Reset ref flags
    initializedRef.current = false;
    fetchingRef.current = false;
    
    toast.success('Logged out successfully');
    
    return loginSource; // Return for redirect
  }, []);

  const getAuthHeader = useCallback(() => {
    const storedToken = localStorage.getItem('token');
    return storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
  }, []);

  // Check if action is allowed (for read-only mode)
  const checkWriteAccess = () => {
    if (isReadOnly) {
      toast.error('Your subscription has expired. Please renew to perform this action.');
      return false;
    }
    return true;
  };

  // Refresh subscription status
  const refreshSubscriptionStatus = useCallback(async () => {
    if (user?.school_id && user?.role !== 'super_admin') {
      await fetchSubscriptionStatus(user.school_id, token);
    }
  }, [user, token]);

  // Check if user has access to a specific module
  const hasModuleAccess = useCallback((moduleId) => {
    if (!userPermissions) return true; // Allow if permissions not loaded yet
    if (userPermissions.has_full_access) return true;
    return userPermissions.modules?.includes(moduleId) || false;
  }, [userPermissions]);

  // Manually refresh auth state (useful after network recovery)
  const refreshAuth = useCallback(async () => {
    if (!token) return;
    fetchingRef.current = false; // Reset flag to allow re-fetch
    await fetchCurrentUser();
  }, [token]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      register, 
      logout, 
      loading, 
      getAuthHeader,
      subscriptionStatus,
      isReadOnly,
      checkWriteAccess,
      refreshSubscriptionStatus,
      userPermissions,
      hasModuleAccess,
      hasTransportAllocation,
      hasHostelAllocation,
      refreshAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
